import React from "react";
import { cn } from "@/lib/utils";

export interface TimePreset {
  label: string;
  time: string; // 24-hour format: "16:00", "18:00", "01:30"
  description?: string;
}

interface PresetTimeSelectorProps {
  presets: TimePreset[];
  value: string;
  onChange: (time: string) => void;
  className?: string;
}

// Format 24-hour time to 12-hour display
const formatTimeFor12Hour = (time24: string): string => {
  if (!time24) return "";
  try {
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  } catch (error) {
    return time24;
  }
};

export const PresetTimeSelector = ({
  presets,
  value,
  onChange,
  className
}: PresetTimeSelectorProps) => {
  return (
    <div className={cn("space-y-3", className)}>
      {presets.map((preset) => {
        const isSelected = value === preset.time;
        return (
          <button
            key={preset.time}
            type="button"
            onClick={() => onChange(preset.time)}
            className={cn(
              "w-full p-4 rounded-xl border-2 text-left transition-all duration-200",
              isSelected
                ? "border-cyan-400 bg-gradient-to-r from-cyan-50 to-green-50 shadow-md"
                : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
            )}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div
                  className={cn(
                    "w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors",
                    isSelected
                      ? "border-cyan-500 bg-cyan-500"
                      : "border-gray-300 bg-white"
                  )}
                >
                  {isSelected && (
                    <div className="w-2 h-2 rounded-full bg-white" />
                  )}
                </div>
                <div>
                  <span className={cn(
                    "font-semibold text-base",
                    isSelected ? "text-cyan-700" : "text-gray-800"
                  )}>
                    {preset.label}
                  </span>
                  {preset.description && (
                    <span className="text-gray-500 text-sm ml-2">
                      — {preset.description}
                    </span>
                  )}
                </div>
              </div>
              <span className={cn(
                "font-medium text-sm",
                isSelected ? "text-cyan-600" : "text-gray-500"
              )}>
                {formatTimeFor12Hour(preset.time)}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
};
